// PRD.329 — Laboratory Departments: identity, validation and ordering.
//
// Pure and dependency-free so it runs under the node:test harness. Department
// codes are DATA, never an enum: the three platform defaults (MICROBIOLOGY,
// PATHOLOGY, BIOCHEMISTRY) are seeded by provisioning, and clinics add their
// own. Nothing in application logic branches on a specific code.

/** A `laboratory_departments` row as PowerSync returns it (booleans are 0/1). */
export interface LaboratoryDepartmentRow {
  id: string;
  org_id: string | null;
  code: string | null;
  name: string | null;
  description: string | null;
  sort_order: number | null;
  active: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export const LABORATORY_DEPARTMENT_CODE_MAX_LENGTH = 40;
export const LABORATORY_DEPARTMENT_NAME_MAX_LENGTH = 80;

/**
 * Canonical code form: upper-case, runs of anything other than A–Z / 0–9
 * collapsed to a single `_`, no leading/trailing `_`. "Pathology / Hematology"
 * → `PATHOLOGY_HEMATOLOGY`. Returns '' for input with no usable characters.
 */
export function normalizeLaboratoryDepartmentCode(raw: string | null | undefined): string {
  return (raw ?? '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/**
 * Deterministic `laboratory_departments.id` for a code.
 *
 * The UI and platform provisioning derive the SAME id, so two offline devices —
 * or a device and a provisioning run — creating one code converge on one row
 * through syncWrite's `ON CONFLICT (id)` upsert. A random id would instead trip
 * the `(org_id, code)` unique index and block that device's upload queue.
 *
 * The org is deliberately NOT part of the id: each organisation has its own
 * database, and `claims.activeOrg` is not guaranteed to equal the provisioning
 * org name, so embedding it would break UI ↔ provisioning convergence. Org
 * scoping lives in the `org_id` column and the `(org_id, code)` index.
 *
 * Provisioning (`provision_org.yml`, the laboratory seed importer) mirrors this
 * exact derivation — keep them in sync.
 */
export function laboratoryDepartmentId(code: string): string {
  return `labdept-${normalizeLaboratoryDepartmentCode(code)}`;
}

export interface LaboratoryDepartmentDraft {
  code: string;
  name: string;
  description?: string | null;
  sortOrder: number | string | null;
}

export type LaboratoryDepartmentErrors = Partial<Record<'code' | 'name' | 'sortOrder', string>>;

/**
 * Validate a create/edit. `existing` is the org's current departments; only
 * rows of `orgId` are considered, so another org's code never blocks this one.
 * On edit the code is immutable (see `laboratoryDepartmentId`), so it is not
 * re-validated — the caller must not send a changed code.
 */
export function validateLaboratoryDepartmentDraft(input: {
  mode: 'create' | 'edit';
  orgId: string;
  draft: LaboratoryDepartmentDraft;
  existing: readonly Pick<LaboratoryDepartmentRow, 'id' | 'org_id' | 'code'>[];
}): { ok: boolean; errors: LaboratoryDepartmentErrors } {
  const errors: LaboratoryDepartmentErrors = {};
  const name = input.draft.name.trim();
  if (!name) errors.name = 'Name is required.';
  else if (name.length > LABORATORY_DEPARTMENT_NAME_MAX_LENGTH) {
    errors.name = `Name must be at most ${LABORATORY_DEPARTMENT_NAME_MAX_LENGTH} characters.`;
  }

  if (input.mode === 'create') {
    const code = normalizeLaboratoryDepartmentCode(input.draft.code);
    if (!code) errors.code = 'Code is required (letters and numbers).';
    else if (code.length > LABORATORY_DEPARTMENT_CODE_MAX_LENGTH) {
      errors.code = `Code must be at most ${LABORATORY_DEPARTMENT_CODE_MAX_LENGTH} characters.`;
    } else if (!/^[A-Z]/.test(code)) {
      errors.code = 'Code must start with a letter.';
    } else {
      const id = laboratoryDepartmentId(code);
      const taken = input.existing.some(
        (d) =>
          (d.org_id == null || d.org_id === input.orgId) &&
          (normalizeLaboratoryDepartmentCode(d.code) === code || d.id === id),
      );
      if (taken) errors.code = `A department with code ${code} already exists.`;
    }
  }

  const sort = parseSortOrder(input.draft.sortOrder);
  if (sort === null) errors.sortOrder = 'Sort order must be a whole number.';

  return { ok: Object.keys(errors).length === 0, errors };
}

/** Parse a sort-order input. Blank → 0; non-integers → null (invalid). */
export function parseSortOrder(raw: number | string | null | undefined): number | null {
  if (raw === null || raw === undefined) return 0;
  const text = String(raw).trim();
  if (text === '') return 0;
  if (!/^-?\d+$/.test(text)) return null;
  return parseInt(text, 10);
}

export function isLaboratoryDepartmentActive(
  row: Pick<LaboratoryDepartmentRow, 'active'>,
): boolean {
  return row.active === 1;
}

/** Departments by `sort_order`, then name (case-insensitive) — PRD.329 R7. */
export function sortLaboratoryDepartments<
  T extends Pick<LaboratoryDepartmentRow, 'sort_order' | 'name'>,
>(rows: readonly T[]): T[] {
  return [...rows].sort(
    (a, b) =>
      (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
      (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' }),
  );
}

/**
 * PRD.329 §4 — the department a service should persist. Converting a service
 * into a non-laboratory service CLEARS its department, so a later re-enable
 * starts unassigned instead of silently resurrecting a stale assignment.
 */
export function resolveServiceLaboratoryDepartmentId(input: {
  isLabTest: boolean;
  laboratoryDepartmentId: string | null | undefined;
}): string | null {
  if (!input.isLabTest) return null;
  return input.laboratoryDepartmentId ? input.laboratoryDepartmentId : null;
}
